import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import { ConsentFieldFormWrapper } from './ConsentFieldFormWrapper';
import { CollapsibleFieldSection } from './CollapsibleFieldSection';
import { StoredFieldsSection } from './StoredFieldsSection';
import { WelcomeModal } from './WelcomeModal';

import { useConsentFormContext } from '../context/ConsentFormContext';
import { useAppSelector } from 'modules/state-mgmt/store';
import { selectCustomConsentAgreements } from 'modules/state-mgmt/cache';
import { WorkerInvitationStatus } from 'models/general';
import { useProjectAcceptance } from '../hooks/useProjectAcceptance';
import { useStyles } from '../styles';

export const ConsentFormPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    nextStep,
    prevStep,
    firstStep,
    companies,
    consentFormFields,
    formValues,
    updateValue,
    validateForm,
    workerId,
    workerUserId,
    projectId,
    projectName,
    languageCode,
    getFormPayloads,
    setIsLoading: setConsentFormLoading,
  } = useConsentFormContext();

  const { isOffline, isWorker, isUnverified } = useAppSelector(state => ({
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    isWorker: !state.user?.userId || state.user?.isWorker,
    isUnverified: state.onboarding?.invitationStatus === WorkerInvitationStatus.NOT_INVITED,
  }));

  const customForms = useAppSelector(state => selectCustomConsentAgreements(state, projectId));
  const hasCustomForms = customForms.length > 0;

  const { executeAcceptance, onboardingStatus, onCloseWelcomeModal, isLoading } = useProjectAcceptance({
    workerId,
    workerUserId,
    projectId,
    projectName,
    languageCode,
    getFormPayloads,
    isUnverified,
    isOffline,
    isWorker,
    firstStep,
  });

  useEffect(() => {
    setConsentFormLoading(isLoading);
  }, [isLoading, setConsentFormLoading]);

  const onBack = useCallback(() => {
    prevStep();
  }, [prevStep]);

  const onNext = useCallback(() => {
    const isValid = validateForm();
    if (!isValid) return;

    if (hasCustomForms) {
      nextStep();
    } else {
      executeAcceptance();
    }
  }, [hasCustomForms, nextStep, validateForm, executeAcceptance]);

  const standardFields = useMemo(
    () => consentFormFields.filter(f => f.consentFormFieldType === 'Standard'),
    [consentFormFields],
  );

  const customFields = useMemo(
    () => consentFormFields.filter(f => f.consentFormFieldType === 'Custom'),
    [consentFormFields],
  );

  return (
    <>
      <div>
        <ContentHeader title={t('consentForm.step2.title')} subtitle={t('consentForm.step2.subtitle')} />
        <div className={classes.sectionSpacer} />
        <CollapsibleFieldSection
          title={t('consentForm.step2.standardFields')}
          count={companies.length}
          classes={classes}
        >
          {companies?.map((company, index) => (
            <div key={company.id}>
              {index > 0 && <Divider />}
              <ConsentFieldFormWrapper
                company={company}
                consentFormFields={standardFields}
                formValues={formValues}
                updateValue={updateValue}
                classes={classes}
              />
            </div>
          ))}
        </CollapsibleFieldSection>
        {customFields.length > 0 && (
          <>
            <div className={classes.sectionGap} />
            <CollapsibleFieldSection
              title={t('consentForm.step2.customFields')}
              count={companies.length}
              classes={classes}
            >
              {companies?.map((company, index) => (
                <div key={company.id}>
                  {index > 0 && <Divider />}
                  <ConsentFieldFormWrapper
                    company={company}
                    consentFormFields={customFields}
                    formValues={formValues}
                    updateValue={updateValue}
                    classes={classes}
                  />
                </div>
              ))}
            </CollapsibleFieldSection>
          </>
        )}
        <StoredFieldsSection />
        <NavigationButtons
          secondaryButtonProps={{
            onClick: onBack,
            testId: 'consent-form-back-button',
          }}
          primaryButtonProps={{
            label: hasCustomForms ? t('common.next') : t('consentForm.step2.completeRegistration'),
            hasEndArrow: true,
            isLoading,
            styleClass: classes.nextButton,
            testId: 'consent-form-next-button',
            onClick: onNext,
          }}
        />
      </div>
      {!!onboardingStatus && (
        <WelcomeModal
          open={!!onboardingStatus}
          onClose={onCloseWelcomeModal}
          projectName={projectName}
          isCertificationComplete={onboardingStatus.isCertificationComplete}
          isCoursesComplete={onboardingStatus.isCoursesComplete}
          isDrugTestingComplete={onboardingStatus.isDrugTestingComplete}
          isUnverified={isUnverified}
        />
      )}
    </>
  );
};
